(() => {
  const pageBody = document.body;

  if (!pageBody) return;

  const debounce = (fn, delay = 120) => {
    let timeoutId;

    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => fn(...args), delay);
    };
  };

  const isProductPage = () => pageBody.classList.contains("template-product");

  const getScrollBarWidth = () => Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const exposeScrollBarWidth = () => {
    document.documentElement.style.setProperty("--scrollbar-width", `${getScrollBarWidth()}px`);
  };

  const isIosDevice = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  const needsScrollLock = () => {
    if (pageBody.classList.contains("overflow-hidden")) return true;

    if (
      document.querySelector(
        'body > .mega-menu-drawer[open], body > .search-popdown[open], body > quick-view.drawer.active, .mini-cart[open], .mini-cart.is-active, .predictive-search[open], .predictive-search.is-open, dialog[open]'
      )
    ) {
      return true;
    }

    const expandedTriggers = document.querySelectorAll(
      '.header [aria-expanded="true"], [data-header] [aria-expanded="true"]'
    );

    return Array.from(expandedTriggers).some((trigger) => {
      if (!(trigger instanceof HTMLElement)) return false;
      if (trigger.classList.contains("collapsible-trigger")) return false;
      if (trigger.closest(".product__accordion, .accordion, [data-product-accordion]")) return false;
      return true;
    });
  };

  const syncScrollLock = () => {
    if (needsScrollLock()) {
      document.documentElement.classList.add("fp-scroll-locked");
    } else {
      document.documentElement.classList.remove("fp-scroll-locked");
    }
  };

  const positionMegaMenu = () => {
    const header = document.querySelector("[data-fp-header], #SiteHeader, .site-header, .shopify-section-header");
    const megaMenu = document.querySelector("body > .mega-menu-drawer");

    if (!header || !megaMenu) return;

    const rect = header.getBoundingClientRect();
    const menuTop =
      pageBody.classList.contains("template-index") && window.scrollY <= 4
        ? Math.max(0, Math.round(rect.top))
        : Math.max(0, Math.round(rect.bottom));

    megaMenu.style.setProperty("top", `${menuTop}px`, "important");
  };

  const normalizeProductViewport = () => {
    if (!isProductPage()) return;

    document
      .querySelectorAll(
        '.shopify-section-group-header-group, .shopify-section-group-announcement-bar-group, [id*="announcement" i], .announcement-bar, .announcement-bar-section, .announcement-bar-wrapper'
      )
      .forEach((node) => {
        if (node instanceof HTMLElement) {
          node.style.setProperty("display", "none", "important");
          node.setAttribute("hidden", "");
        }
      });

    const header =
      document.querySelector('[data-fp-header-shell]') ||
      document.querySelector('#shopify-section-header [data-fp-header]') ||
      document.querySelector('#shopify-section-header .header');

    const height = Math.max(48, Math.round(header?.getBoundingClientRect().height || 56));

    document.documentElement.style.setProperty("--fp-header-height", `${height}px`);
    pageBody.setAttribute("data-product-header-reclaimed", "true");
  };

  const hideReviewApps = () => {
    /*
      Review app blocks must remain available. Product/card rating summaries
      are controlled by scoped CSS elsewhere; stability code must never hide
      Judge.me, Trustoo, CWILL, or any other review app widget.
    */
  };

  const markProductControls = () => {
    if (!isProductPage()) return;

    document.querySelectorAll('button, a, summary, input, select, textarea').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      const context = node.closest(
        '.product, .product-form, .product__info-container, .product__media-wrapper, [data-product-form], [data-product-gallery]'
      );

      if (!context) return;
      node.setAttribute("data-product-native-control", "true");
    });
  };

  const transferLegacyProductData = () => {
    if (!isProductPage()) return;

    const dataSources = document.querySelectorAll(
      ".product,[data-product-id],[data-product-handle],[data-product-url],[data-product-json],[data-product-container]"
    );

    if (!dataSources.length) return;

    const fieldsToCopy = ["productId", "productHandle", "productUrl", "sectionId", "productJson"];

    document
      .querySelectorAll(
        ".main-product,.product-section,[data-product-form],[data-product-gallery],product-form,product-gallery,media-gallery"
      )
      .forEach((target) => {
        if (!(target instanceof HTMLElement)) return;

        for (const source of dataSources) {
          if (!(source instanceof HTMLElement)) continue;

          fieldsToCopy.forEach((field) => {
            if (!target.dataset[field] && source.dataset[field]) {
              target.dataset[field] = source.dataset[field];
            }
          });
        }
      });
  };

  const normalizeAria = () => {
    document
      .querySelectorAll('button, a, summary, input, select, textarea, [role="button"], [tabindex]')
      .forEach((node) => {
        if (!(node instanceof HTMLElement)) return;

        if (node.getAttribute("aria-hidden") === "true") {
          node.removeAttribute("tabindex");
          node.setAttribute("inert", "");
          return;
        }

        if (node.hasAttribute("inert")) {
          node.removeAttribute("inert");
        }

        if (node.getAttribute("tabindex") === "-1" && !node.matches('[disabled], [aria-disabled="true"]')) {
          const insideClosedDetails = node.closest("details:not([open])");
          const insideDrawer = node.closest(
            '.mega-menu-drawer:not([open]), .mini-cart:not([open]), quick-view.drawer:not(.active), .predictive-search:not([open]):not(.is-open)'
          );

          if (!insideClosedDetails && !insideDrawer) {
            node.setAttribute("tabindex", "0");
          }
        }
      });
  };

  const guardHoverThenClick = () => {
    document.addEventListener(
      "pointerover",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const anchor = target.closest("a[href]");
        if (!(anchor instanceof HTMLAnchorElement)) return;

        if (anchor.dataset.fpHref) return;
        anchor.dataset.fpHref = anchor.href;
      },
      true
    );

    document.addEventListener(
      "pointerout",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const anchor = target.closest("a[data-fp-href]");
        if (!(anchor instanceof HTMLAnchorElement)) return;

        if (!anchor.href && anchor.dataset.fpHref) {
          anchor.href = anchor.dataset.fpHref;
        }
      },
      true
    );
  };

  const guardProductControls = () => {
    if (!isProductPage()) return;

    document.addEventListener(
      "click",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const control = target.closest("[data-product-native-control]");
        if (!(control instanceof HTMLElement)) return;

        if (control.matches('a[href]') && !control.getAttribute("href")) {
          const storedHref = control.getAttribute("data-fp-href");
          if (storedHref) control.setAttribute("href", storedHref);
        }
      },
      true
    );
  };

  const stabilizeStatic = () => {
    exposeScrollBarWidth();
    syncScrollLock();
    positionMegaMenu();
    normalizeProductViewport();
    hideReviewApps();
    markProductControls();
    transferLegacyProductData();
    normalizeAria();
  };

  const delayedStabilize = debounce(stabilizeStatic, 100);

  guardHoverThenClick();
  guardProductControls();
  stabilizeStatic();

  window.addEventListener("resize", delayedStabilize, { passive: true });
  window.addEventListener("pageshow", stabilizeStatic);
  document.addEventListener("shopify:section:load", stabilizeStatic);
  document.addEventListener("shopify:section:reorder", delayedStabilize);

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (
      target.closest(
        '.header, [data-header], .mega-menu-drawer, .search-popdown, .mini-cart, quick-view.drawer, .predictive-search'
      )
    ) {
      window.requestAnimationFrame(() => {
        syncScrollLock();
        positionMegaMenu();
      });
    }
  });

  if (isIosDevice()) {
    pageBody.setAttribute("data-ios-device", "true");
  }
})();